import React, { useMemo, useState } from "react";
import { InBodyAds } from "@bloomberg/media-ui-ads";
import { createClasses } from "@bloomberg/media-ui-core";
import { useIntl } from "react-intl";
import styles from "./style.module.css";
import type { SectionFrontPage } from "~types/types";
import SectionFrontTable from "~components/SectionFront/SectionFrontTable";
import SectionFrontDisclaimer from "~components/SectionFront/SectionFrontDisclaimer";
import { getTableColumns } from "~components/SectionFront/sectionFrontTableColumns";
import BfixHeader from "~components/SectionFront/BfixHeader";
import { useSectionFrontData } from "~hooks/SectionFront/useSectionFrontData";
import { useBfixRatesData } from "~hooks/SectionFront/useBfixRatesData";
import { useLocale } from "~hooks/i18n/useLocale";

const DEFAULT_COLUMNS_ON_MOBILE = 3;

type SectionFrontProps = {
  sectionFrontPage: SectionFrontPage;
};

function SectionFrontAd() {
  return (
    <>
      <InBodyAds
        className={styles.inBodyAd}
        positionIncrement={1}
        hideEmptyAd
      />
    </>
  );
}

export default function SectionFront(props: SectionFrontProps) {
  const { sectionFrontPage } = props;
  const { sectionFrontTab } = sectionFrontPage;
  const { isJapanese } = useLocale();

  const [sectionFrontOptions, setSectionFrontOptions] = useState<
    { bfixDateString: string } | undefined
  >(undefined);

  const { locale } = useIntl();

  const { data: updatedSections = [] } = useSectionFrontData(
    sectionFrontPage?.sectionFrontName,
    sectionFrontTab?.identifier,
    locale
  );

  const { data: bfixRates = [], status } = useBfixRatesData(
    sectionFrontOptions?.bfixDateString
  );

  const sections = useMemo(() => {
    const newSections = updatedSections?.length
      ? updatedSections
      : sectionFrontTab?.sections || [];

    // handle special case for bfix rates section to replace securities with fetched bfix rates
    if (sectionFrontTab.identifier === "fx-fixings" && bfixRates?.length) {
      return newSections.map((section) => {
        if (bfixRates?.length) {
          // to retain recently fetched rates, otherwise empty rates will flash when changing bfix date/region
          // eslint-disable-next-line no-param-reassign
          section.securities = bfixRates;
        }

        return section;
      });
    }

    return newSections;
  }, [
    updatedSections,
    sectionFrontTab?.sections,
    sectionFrontTab.identifier,
    bfixRates,
  ]);

  const sectionHeader =
    sectionFrontPage.sectionFrontName === "fx-fixings" ? (
      <BfixHeader onBfixDateChange={setSectionFrontOptions} />
    ) : null;

  const cx = createClasses([
    styles.sectionFront,
    {
      [styles.loading]: status === "pending",
    },
  ]);

  return (
    <div className={cx}>
      {sections.map((section, index) => (
        <div className={styles.section} key={index}>
          <SectionFrontTable
            columns={getTableColumns(section.columns, isJapanese)}
            rows={section.securities}
            maxColumnsOnMobile={
              section.maxColumnsOnMobile ?? DEFAULT_COLUMNS_ON_MOBILE
            }
            sectionHeader={sectionHeader}
            sectionUrl={section.url}
            subtitle={section.subtitle}
            title={section.title}
            disclaimer={section.disclaimer}
          />

          {index === 0 && <SectionFrontAd />}
        </div>
      ))}
      <SectionFrontDisclaimer
        sectionFrontName={sectionFrontPage.sectionFrontName}
        selectedTab={sectionFrontTab.identifier}
        variant="bottom"
      />
    </div>
  );
}
